import ViewOnScreen from '../../components/view-on-screen/ViewOnScreen';
import dayjs from 'dayjs';
import { CartItem, CartOperationType } from '../../types/cart';
import { dispatch, RootState, useAppSelector } from '../../redux/store';
import { openCartModal, setCartItem } from '../../redux/slice/cartSlice';
import { redirectToCart, redirectToCategories, redirectToEquipmentClass } from '../../utils/router';
import { useActivateCart } from '../../utils/activateCart';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import NoIconImage from '../../assets/placeholder-image-small.png';
import {
  CartItemsContainer,
  CartHeaderItem,
  CartItemImageWrapper,
  CartItemImage,
  CartItemDetailsContainer,
  CartItemName,
  CartItemUpdateContainer,
  StyledPencilIcon,
  StyledDeleteIcon,
  DateSummaryContainer,
  DateSummaryItem,
  DateSummaryLabel,
  DateSummaryValue,
  EstimatedTotalContainer,
  EstimatedTotalLabel,
  EstimatedTotal,
  CheckoutButton,
  ContinueShoppingButton,
  BrowseEquipmentButton,
  BrowseSelectionLabel,
} from './CartHeaderStyle';

export default function CartHeaderContent() {
  const cartItemsContainerRef = useRef<HTMLDivElement>(null);
  const elementsRefs: React.RefObject<HTMLDivElement>[] = [];
  const config = useAppSelector((state: RootState) => state.cart.cartHeaderConfig);
  const cart = useAppSelector((state: RootState) => state.cart);
  const activateCart = useActivateCart();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean | undefined>(false);
  const [index, setIndex] = useState<number | undefined>();
  const [maxItemsToShow, setMaxItemsToShow] = useState<number | undefined>();

  const theme = useTheme();
  const isSmallScreenBreakpoint = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.xs),
  );

  useEffect(() => {
    setOpen(config?.open);
    setIndex(config?.index);
    setMaxItemsToShow(config?.maxItemsToShow);
  }, [config]);

  useEffect(() => {
    const isLoading = Object.values(cart.loadingFlags).some((status) => status);
    setIsLoading(isLoading);
  }, [cart.loadingFlags]);

  useEffect(() => {
    if (open && cartItemsContainerRef.current) {
      const cartItemsContainer = cartItemsContainerRef.current;
      if (cart.cartItems.length == 0) {
        cartItemsContainer.scrollTop = cartItemsContainer.scrollHeight;
      } else {
        let item: HTMLElement;
        if (maxItemsToShow && cart.cartItems.length > maxItemsToShow) {
          item = cartItemsContainer.querySelector(
            `#cart-item-${String(maxItemsToShow)}`,
          ) as HTMLElement;
          const itemOffset = item.offsetTop;
          const itemHeight = item.offsetHeight;
          const itemMiddle = itemOffset - itemHeight / 1.3;
          cartItemsContainer.style.maxHeight = `${itemMiddle}px`;
        }
        if (index && index !== -1) {
          item = cartItemsContainer.querySelector(`#cart-item-${String(index)}`) as HTMLElement;
        } else {
          item = cartItemsContainer.querySelector(
            `#cart-item-${String(cart.cartItems.length - 1)}`,
          ) as HTMLElement;
        }
        const itemOffset = item.offsetTop;
        const cartItemsContainerScrollTop = cartItemsContainer.scrollTop;
        const cartItemsContainerOffset = cartItemsContainer.getBoundingClientRect().top;
        const offset = itemOffset - cartItemsContainerOffset + cartItemsContainerScrollTop;
        if (!isSmallScreenBreakpoint) {
          cartItemsContainer.scrollTo({
            top: offset,
            //behavior: 'smooth',
          });
        }
      }
    }
  }, [open]);

  const handleCartLinkClick = (slug: string) => {
    redirectToEquipmentClass(slug);
  };

  const handleEdit = (cartItem: CartItem, index: number) => {
    dispatch(
      openCartModal({
        index,
        cartItem,
        cartOperationType: CartOperationType.UPDATE,
        toggleCartHeaderOnUpdate: true,
      }),
    );
  };

  const handleDelete = (index: number) => {
    const payload = {
      index: index,
      actionType: CartOperationType.REMOVE,
    };
    dispatch(setCartItem(payload));
  };

  useEffect(() => {
    open && activateCart();
  }, [open]);

  const renderDateSummary = () => {
    if (cart.cartItems.length === 0) return null;
    const firstItem = cart.cartItems[0];
    const start = dayjs.unix(firstItem.startDate);
    const end = dayjs.unix(firstItem.endDate);
    return (
      <DateSummaryContainer>
        <DateSummaryItem>
          <DateSummaryLabel>Drop Off: </DateSummaryLabel>
          <DateSummaryValue>
            {start.format('MM/DD/YYYY')} ({start.format('h:mmA')})
          </DateSummaryValue>
        </DateSummaryItem>
        <DateSummaryItem>
          <DateSummaryLabel>Pick Up: </DateSummaryLabel>
          <DateSummaryValue>
            {end.format('MM/DD/YYYY')} ({end.format('h:mmA')})
          </DateSummaryValue>
        </DateSummaryItem>
      </DateSummaryContainer>
    );
  };

  return cart.cartItems.length > 0 ? (
    <>
      <CartItemsContainer ref={cartItemsContainerRef}>
        {cart.cartItems.map((item: CartItem, index: number) => {
          return (
            <CartHeaderItem
              id={`cart-item-${String(index)}`}
              key={String(index) + ':' + String(item.equipmentId)}
              ref={(ref) => (elementsRefs[index] = ref as React.RefObject<HTMLDivElement>)}
            >
              <CartItemImageWrapper>
                <CartItemImage
                  src={item.imageSource ?? ''}
                  alt={`image for ${item.name}`}
                  onError={(e) => {
                    const imageElement = e.target as HTMLImageElement;
                    imageElement.src = NoIconImage;
                  }}
                />
              </CartItemImageWrapper>
              <CartItemDetailsContainer>
                <CartItemName onClick={() => handleCartLinkClick(item.slug as string)}>
                  {item.quantity}
                  {' x '}
                  {item.name ?? ''}
                </CartItemName>
              </CartItemDetailsContainer>
              <CartItemUpdateContainer>
                <StyledPencilIcon onClick={() => handleEdit(item, index)}></StyledPencilIcon>
                <StyledDeleteIcon onClick={() => handleDelete(index)} />
              </CartItemUpdateContainer>
            </CartHeaderItem>
          );
        })}
      </CartItemsContainer>
      {renderDateSummary()}
      <EstimatedTotalContainer>
        <EstimatedTotalLabel>Subtotal</EstimatedTotalLabel>
        <EstimatedTotal amount={cart.quote?.rental_subtotal} isLoading={isLoading} />
      </EstimatedTotalContainer>
      <CheckoutButton data-element-id='header-view-cart-button' onClick={redirectToCart}>View Cart & Checkout</CheckoutButton>
      <ContinueShoppingButton data-element-id='header-continue-shopping-button' onClick={redirectToCategories}>
        Continue Shopping
      </ContinueShoppingButton>
    </>
  ) : (
    <>
      <ViewOnScreen screen={{ down: 'xs' }}>
        <BrowseSelectionLabel>Get started by browsing our selection</BrowseSelectionLabel>
      </ViewOnScreen>
      <BrowseEquipmentButton onClick={redirectToCategories}>browse equipment</BrowseEquipmentButton>
    </>
  );
}
